import React from 'react';
import { Link } from 'react-router-dom';
import './AccountingInfoPage.css';

const AccountingInfoPage: React.FC = () => {
  return (
    <div className="accounting-info-page">
      <div className="hero-section accounting">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Professional Accounting System</h1>
            <p className="hero-description">Complete double-entry bookkeeping with journal entries, chart of accounts, and financial reporting. Built for small businesses and accounting professionals.</p>
            <div className="cta-buttons">
              <Link to="/accounting" className="btn btn-primary btn-large">Open Accounting</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>Core Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>Journal Entries</h3>
              <p>Create balanced journal entries with multiple account lines and automatic validation.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📁</div>
              <h3>Chart of Accounts</h3>
              <p>Organize accounts by type (Assets, Liabilities, Equity, Revenue, Expenses).</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏢</div>
              <h3>Multi-Business</h3>
              <p>Manage multiple businesses with separate books and user permissions.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Financial Reports</h3>
              <p>Trial balance, balance sheet, and income statement generation.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚖️</div>
              <h3>Double Entry</h3>
              <p>Automatic balance validation ensures accounting equation integrity.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔧</div>
              <h3>Business Settings</h3>
              <p>Configure business information, fiscal year, currency, and account structure.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="workflow-section">
        <div className="container">
          <h2>How It Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <h3>Create Your Business</h3>
              <p>Set up business profile with currency, fiscal year, and contact information.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <h3>Set Up Chart of Accounts</h3>
              <p>Default accounts are created automatically, or customize to your needs.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <h3>Record Transactions</h3>
              <p>Enter journal entries with debits and credits for all business activities.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">4</div>
              <h3>Generate Reports</h3>
              <p>View trial balance, balance sheet, and income statements instantly.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Access Levels</h2>
          <div className="pricing-grid">
            <div className="pricing-card free">
              <h3>Demo Mode</h3>
              <div className="price">Free</div>
              <ul>
                <li>✅ Explore all features</li>
                <li>✅ Sample business data</li>
                <li>✅ Full functionality preview</li>
                <li>❌ Cannot save data</li>
              </ul>
              <Link to="/accounting" className="btn btn-primary">Try Demo</Link>
            </div>
            <div className="pricing-card account">
              <h3>Registered User</h3>
              <div className="price">Free</div>
              <ul>
                <li>✅ Create businesses</li>
                <li>✅ Save journal entries</li>
                <li>✅ Export reports</li>
                <li>✅ Multi-user access</li>
              </ul>
              <Link to="/account" className="btn btn-secondary">Create Account</Link>
            </div>
            <div className="pricing-card supporter">
              <h3>Supporters Club</h3>
              <div className="price">$10/year</div>
              <ul>
                <li>✅ Everything in Free</li>
                <li>✅ Up to 10 businesses (Free: 1)</li>
                <li>✅ 10,000 journal entries (Free: 500)</li>
                <li>✅ Unlimited calculator scenarios and scheduling Pro</li>
                <li>✅ Priority support</li>
              </ul>
              <Link to="/account" className="btn btn-premium">Join Supporters Club</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is this a full accounting system?</h3>
              <p>Yes, it follows double-entry bookkeeping principles with proper journal entries, chart of accounts, and simple financial reporting.</p>
            </div>
            <div className="faq-item">
              <h3>Can multiple users access the same business?</h3>
              <p>Yes, businesses support multiple users with different permission levels for collaboration.</p>
            </div>
            <div className="faq-item">
              <h3>What currencies are supported?</h3>
              <p>Currently supports CAD and USD with proper formatting and currency symbols.</p>
            </div>
            <div className="faq-item">
              <h3>Is my data secure?</h3>
              <p>Yes, all data is stored securely with user authentication and business-level access controls.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Tool Recommendations */}
      <section style={{padding: '3rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
          <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <Link to="/tax-tools" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Tax Tools</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Estimate income taxes and calculate payroll deductions</p>
              </div>
            </Link>
            <Link to="/scheduling" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Scheduling</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Create employee schedules and manage shifts</p>
              </div>
            </Link>
            <Link to="/rrsp" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Retirement Planner</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Plan your retirement with RRSP, TFSA, CPP, and OAS projections</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Start Managing Your Books</h2>
          <p>Professional accounting tools made simple and accessible.</p>
          <div className="cta-buttons">
            <Link to="/accounting" className="btn btn-primary btn-large">Open Accounting System</Link>
            <Link to="/account" className="btn btn-secondary btn-large">Create Free Account</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountingInfoPage;